import React, { useState, useEffect, useRef } from 'react';
import './CancelarRegistrosModal.css';

export interface RegistroParaCancelar {
    id: number;
    idRegistradoraContrato: string;
    numeroContrato: string;
}

type ModalStatus = 'idle' | 'loading' | 'success' | 'error';

interface CancelarRegistrosModalProps {
    show: boolean;
    onClose: () => void;
    onConfirm: (razaoCancelamento: string) => Promise<void>;
    registros: RegistroParaCancelar[];
    onSuccess?: () => void;
}

const CancelarRegistrosModal: React.FC<CancelarRegistrosModalProps> = ({
    show,
    onClose,
    onConfirm,
    registros,
    onSuccess,
}) => {
    const [status, setStatus] = useState<ModalStatus>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [razaoCancelamento, setRazaoCancelamento] = useState('');
    const prevShowRef = useRef(false);

    useEffect(() => {
        if (show && !prevShowRef.current) {
            setStatus('idle');
            setErrorMessage('');
            setRazaoCancelamento('');
        }
        prevShowRef.current = show;
    }, [show]);

    const isLoading = status === 'loading';

    const handleConfirm = async () => {
        if (!razaoCancelamento.trim()) return;
        setStatus('loading');
        setErrorMessage('');
        try {
            await onConfirm(razaoCancelamento.trim());
            setStatus('success');
        } catch (err) {
            setStatus('error');
            setErrorMessage(err instanceof Error ? err.message : 'Erro desconhecido ao cancelar registros.');
        }
    };

    if (!show) return null;

    return (
        <div className="cancelar-modal-overlay" onClick={isLoading ? undefined : onClose}>
            <div className="cancelar-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="cancelar-modal-header">
                    <h3>Confirmar Cancelamento de Registros</h3>
                    {!isLoading && <button className="cancelar-modal-close" onClick={onClose}>×</button>}
                </div>

                {status === 'loading' && (
                    <div className="cancelar-status-container">
                        <div className="cancelar-spinner" />
                        <p className="cancelar-status-text">Processando cancelamento...</p>
                    </div>
                )}

                {status === 'success' && (
                    <div className="cancelar-status-container">
                        <div className="cancelar-status-icon cancelar-status-icon--success">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                <path d="M7 12.5L10.5 16L17 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <p className="cancelar-status-text cancelar-status-text--success">Registros cancelados com sucesso!</p>
                        <div className="cancelar-modal-footer">
                            <button
                                className="cancelar-modal-button cancelar-modal-button-confirm"
                                onClick={() => { onClose(); onSuccess?.(); }}
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                )}

                {status === 'error' && (
                    <div className="cancelar-status-container">
                        <div className="cancelar-status-icon cancelar-status-icon--error">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                <path d="M8 8L16 16M16 8L8 16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <p className="cancelar-status-text cancelar-status-text--error">Erro ao cancelar registros</p>
                        {errorMessage && <p className="cancelar-status-detail">{errorMessage}</p>}
                        <div className="cancelar-modal-footer">
                            <button className="cancelar-modal-button cancelar-modal-button-cancel" onClick={() => setStatus('idle')}>
                                Tentar novamente
                            </button>
                            <button className="cancelar-modal-button cancelar-modal-button-confirm" onClick={onClose}>
                                Fechar
                            </button>
                        </div>
                    </div>
                )}

                {status === 'idle' && (
                    <>
                        <div className="cancelar-modal-body">
                            <div className="cancelar-warning-icon">
                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                            <p className="cancelar-modal-message">
                                Você está prestes a cancelar <strong>{registros.length}</strong> {registros.length === 1 ? 'registro' : 'registros'}.
                            </p>
                            <div className="cancelar-registros-list">
                                <p className="cancelar-list-title">Registros selecionados:</p>
                                <div className="cancelar-ids-container">
                                    {registros.map(r => (
                                        <span key={r.id} className="cancelar-registro-badge">{r.numeroContrato}</span>
                                    ))}
                                </div>
                            </div>

                            <div className="cancelar-form-group">
                                <label className="cancelar-form-label" htmlFor="razao-cancelamento">
                                    Razão do Cancelamento <span className="cancelar-required">*</span>
                                </label>
                                <textarea
                                    id="razao-cancelamento"
                                    className="cancelar-form-textarea"
                                    value={razaoCancelamento}
                                    onChange={(e) => setRazaoCancelamento(e.target.value)}
                                    placeholder="Informe a razão do cancelamento"
                                    rows={3}
                                />
                            </div>

                            <p className="cancelar-modal-warning">
                                Esta ação não pode ser desfeita. Deseja continuar?
                            </p>
                        </div>
                        <div className="cancelar-modal-footer">
                            <button
                                className="cancelar-modal-button cancelar-modal-button-cancel"
                                onClick={onClose}
                            >
                                Voltar
                            </button>
                            <button
                                className="cancelar-modal-button cancelar-modal-button-danger"
                                onClick={handleConfirm}
                                disabled={!razaoCancelamento.trim()}
                            >
                                Confirmar Cancelamento
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CancelarRegistrosModal;
